import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Heart, Calendar, Pencil, Trash2 } from 'lucide-react';
import { useCharacter } from '../hooks/useCharacter';
import { useAuth } from '../hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../lib/supabase';

export default function CharacterDetails() {
  const { ideaId = '', characterId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { character, loading, error, toggleFavorite } = useCharacter(characterId);
  const [isFavoriting, setIsFavoriting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleFavorite = async () => {
    if (!character) return;
    try {
      setIsFavoriting(true);
      await toggleFavorite();
    } finally {
      setIsFavoriting(false);
    }
  };

  const handleDelete = async () => {
    if (!character || !window.confirm('Are you sure you want to delete this character? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      const { error: deleteError } = await supabase
        .from('characters')
        .delete()
        .eq('id', characterId);

      if (deleteError) throw deleteError;
      navigate(`/idea/${ideaId}`);
    } catch (err) {
      console.error('Error deleting character:', err);
      alert('Failed to delete character. Please try again.');
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !character) {
    return (
      <div className="max-w-4xl mx-auto p-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error || 'Character not found'}
      </div>
    );
  }

  // Check if user is the author of the book idea
  const isAuthor = user?.id === character.author_id;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="flex items-center justify-between mb-6">
        <Link 
          to={`/idea/${ideaId}`}
          className="inline-flex items-center gap-2 text-accent1 hover:text-accent2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Story
        </Link>

        {isAuthor && (
          <div className="flex items-center gap-2">
            <Link
              to={`/idea/${ideaId}/character/${characterId}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <Pencil className="w-4 h-4" />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-8">
          <div className="flex items-start gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {character.avatar_url ? (
                <img
                  src={character.avatar_url}
                  alt={character.name}
                  className="w-32 h-32 rounded-full object-cover"
                />
              ) : (
                <div 
                  className="w-32 h-32 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--color-accent3)' }}
                >
                  <User 
                    className="w-16 h-16"
                    style={{ color: 'var(--color-accent1)' }}
                  />
                </div>
              )}
            </div>

            {/* Character Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold" style={{ color: 'var(--color-font)' }}>
                    {character.name}
                  </h1>
                  <p 
                    className="text-lg mt-1"
                    style={{ color: 'var(--color-accent1)' }}
                  >
                    {character.role}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span 
                      className="flex items-center gap-1"
                      style={{ color: 'var(--color-accent2)' }}
                    >
                      <Calendar className="w-4 h-4" />
                      Added {formatDistanceToNow(new Date(character.created_at), { addSuffix: true })}
                    </span>
                    <span style={{ color: 'var(--color-accent2)' }}>
                      {character.favorite_count} {character.favorite_count === 1 ? 'favorite' : 'favorites'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleFavorite}
                  disabled={isFavoriting}
                  className={`p-2 rounded-full transition-colors ${
                    character.has_favorited
                      ? 'text-red-500 hover:bg-red-50'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart 
                    className="w-6 h-6" 
                    fill={character.has_favorited ? "currentColor" : "none"}
                  />
                </button>
              </div>

              <div className="mt-6 prose max-w-none" style={{ color: 'var(--color-font)' }}>
                <h2 className="text-xl font-semibold mb-2">Description</h2>
                <p className="whitespace-pre-wrap">{character.description}</p>
              </div>

              {/* Additional character details */}
              {(character.personality || character.background || character.goals || character.relationships) && (
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {character.personality && (
                    <div>
                      <h2 
                        className="text-xl font-semibold mb-2"
                        style={{ color: 'var(--color-font)' }}
                      >
                        Personality
                      </h2>
                      <p 
                        className="whitespace-pre-wrap"
                        style={{ color: 'var(--color-font)' }}
                      >
                        {character.personality}
                      </p>
                    </div>
                  )}

                  {character.background && (
                    <div>
                      <h2 
                        className="text-xl font-semibold mb-2"
                        style={{ color: 'var(--color-font)' }}
                      >
                        Background
                      </h2>
                      <p 
                        className="whitespace-pre-wrap"
                        style={{ color: 'var(--color-font)' }}
                      >
                        {character.background}
                      </p>
                    </div>
                  )}

                  {character.goals && (
                    <div>
                      <h2 
                        className="text-xl font-semibold mb-2"
                        style={{ color: 'var(--color-font)' }}
                      >
                        Goals & Motivations
                      </h2>
                      <p 
                        className="whitespace-pre-wrap"
                        style={{ color: 'var(--color-font)' }}
                      >
                        {character.goals}
                      </p>
                    </div>
                  )}

                  {character.relationships && (
                    <div>
                      <h2 
                        className="text-xl font-semibold mb-2"
                        style={{ color: 'var(--color-font)' }}
                      >
                        Relationships
                      </h2>
                      <p 
                        className="whitespace-pre-wrap"
                        style={{ color: 'var(--color-font)' }}
                      >
                        {character.relationships}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}